import { useState, useCallback } from 'react';
import { unzipSync } from 'fflate';

// Bambu Lab A1 build volume in mm
const MAX_BUILD_MM = 256;

interface ParsedVolume {
  readonly volumeCm3: number;
  readonly boundingBox: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  };
}

interface VolumeEstimate extends ParsedVolume {
  readonly exceedsBuildVolume: boolean;
}

export const useModelVolume = () => {
  const [estimate, setEstimate] = useState<VolumeEstimate | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculate = useCallback(async (file: File): Promise<VolumeEstimate | null> => {
    const ext = file.name.toLowerCase().split('.').pop() ?? '';

    // Client-side volume estimation only for 3MF and binary STL
    if (ext !== '3mf' && ext !== 'stl') {
      return null;
    }

    setIsCalculating(true);

    try {
      const buffer = await file.arrayBuffer();
      const parsed = ext === '3mf'
        ? parse3mfVolume(new Uint8Array(buffer))
        : parseStlVolume(buffer);

      // Check if any dimension exceeds the build plate (bounding box is in cm)
      const exceedsBuildVolume =
        parsed.boundingBox.width * 10 > MAX_BUILD_MM ||
        parsed.boundingBox.height * 10 > MAX_BUILD_MM ||
        parsed.boundingBox.depth * 10 > MAX_BUILD_MM;

      const result = { ...parsed, exceedsBuildVolume };
      setEstimate(result);
      return result;
    } catch {
      setEstimate(null);
      return null;
    } finally {
      setIsCalculating(false);
    }
  }, []);

  const reset = useCallback(() => {
    setEstimate(null);
  }, []);

  return { estimate, isCalculating, calculate, reset };
};

/**
 * Client-side 3MF parser for rough volume estimation.
 * 3MF is a ZIP archive containing XML with mesh data.
 * Bambu Studio 3MF files may split objects into separate .model files.
 */
const parse3mfVolume = (data: Uint8Array): ParsedVolume => {
  const zip = unzipSync(data);

  // Find ALL .model files (Bambu Studio splits objects into 3D/Objects/*.model)
  const modelKeys = Object.keys(zip).filter((name) =>
    name.toLowerCase().endsWith('.model'),
  );

  if (modelKeys.length === 0) {
    throw new Error('No .model file found in 3MF archive');
  }

  // Parse vertices and triangles from all model files
  const vertices: number[][] = [];
  const triangles: { v1: number; v2: number; v3: number }[] = [];

  for (const modelKey of modelKeys) {
    const xml = new TextDecoder().decode(zip[modelKey]);
    parseMeshFromXml(xml, vertices, triangles);
  }

  if (vertices.length === 0 || triangles.length === 0) {
    throw new Error('3MF file contains no mesh data');
  }

  // Compute volume and bounding box
  let volume = 0;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  for (const [x, y, z] of vertices) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }

  for (const tri of triangles) {
    const [v1x, v1y, v1z] = vertices[tri.v1];
    const [v2x, v2y, v2z] = vertices[tri.v2];
    const [v3x, v3y, v3z] = vertices[tri.v3];

    volume +=
      (v1x * (v2y * v3z - v3y * v2z) -
        v2x * (v1y * v3z - v3y * v1z) +
        v3x * (v1y * v2z - v2y * v1z)) /
      6.0;
  }

  // 3MF coordinates are in mm
  const volumeCm3 = Math.abs(volume) / 1000; // mm3 -> cm3

  return {
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    boundingBox: {
      width: Math.round(((maxX - minX) / 10) * 100) / 100,  // mm -> cm
      height: Math.round(((maxY - minY) / 10) * 100) / 100,
      depth: Math.round(((maxZ - minZ) / 10) * 100) / 100,
    },
  };
};

/** Extract vertices and triangles from a single .model XML string */
const parseMeshFromXml = (
  xml: string,
  vertices: number[][],
  triangles: { v1: number; v2: number; v3: number }[],
): void => {
  // Use indexOf to find <mesh>...</mesh> blocks (regex overflows on large files)
  const meshOpenTag = '<mesh>';
  const meshCloseTag = '</mesh>';
  let searchFrom = 0;

  while (true) {
    const meshStart = xml.indexOf(meshOpenTag, searchFrom);
    if (meshStart === -1) break;
    const contentStart = meshStart + meshOpenTag.length;
    const meshEnd = xml.indexOf(meshCloseTag, contentStart);
    if (meshEnd === -1) break;

    const meshContent = xml.substring(contentStart, meshEnd);
    searchFrom = meshEnd + meshCloseTag.length;

    const vertexOffset = vertices.length;

    const vertexRegex = /<vertex\s+x="([^"]+)"\s+y="([^"]+)"\s+z="([^"]+)"\s*\/>/g;
    let vMatch: RegExpExecArray | null;
    while ((vMatch = vertexRegex.exec(meshContent)) !== null) {
      vertices.push([
        parseFloat(vMatch[1]),
        parseFloat(vMatch[2]),
        parseFloat(vMatch[3]),
      ]);
    }

    const triRegex = /<triangle\s+v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"[^/]*\/>/g;
    let tMatch: RegExpExecArray | null;
    while ((tMatch = triRegex.exec(meshContent)) !== null) {
      triangles.push({
        v1: parseInt(tMatch[1], 10) + vertexOffset,
        v2: parseInt(tMatch[2], 10) + vertexOffset,
        v3: parseInt(tMatch[3], 10) + vertexOffset,
      });
    }
  }
};

/**
 * Client-side binary STL parser for rough volume estimation.
 * Binary STL: 80-byte header, 4-byte triangle count, then 50 bytes per triangle
 * (normal[3 floats] + vertex1[3] + vertex2[3] + vertex3[3] + attribute[2 bytes]).
 * Coordinates are in mm (standard for 3D printing STL files).
 */
const parseStlVolume = (buffer: ArrayBuffer): ParsedVolume => {
  const view = new DataView(buffer);
  const triangleCount = view.getUint32(80, true);

  let volume = 0;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  for (let i = 0; i < triangleCount; i++) {
    // Each triangle: 12 bytes normal + 36 bytes vertices + 2 bytes attribute = 50 bytes
    const offset = 84 + i * 50;

    // Skip normal (12 bytes), read 3 vertices (9 floats)
    const v1x = view.getFloat32(offset + 12, true);
    const v1y = view.getFloat32(offset + 16, true);
    const v1z = view.getFloat32(offset + 20, true);
    const v2x = view.getFloat32(offset + 24, true);
    const v2y = view.getFloat32(offset + 28, true);
    const v2z = view.getFloat32(offset + 32, true);
    const v3x = view.getFloat32(offset + 36, true);
    const v3y = view.getFloat32(offset + 40, true);
    const v3z = view.getFloat32(offset + 44, true);

    // Bounding box
    minX = Math.min(minX, v1x, v2x, v3x);
    minY = Math.min(minY, v1y, v2y, v3y);
    minZ = Math.min(minZ, v1z, v2z, v3z);
    maxX = Math.max(maxX, v1x, v2x, v3x);
    maxY = Math.max(maxY, v1y, v2y, v3y);
    maxZ = Math.max(maxZ, v1z, v2z, v3z);

    // Signed volume via divergence theorem
    volume +=
      (v1x * (v2y * v3z - v3y * v2z) -
        v2x * (v1y * v3z - v3y * v1z) +
        v3x * (v1y * v2z - v2y * v1z)) /
      6.0;
  }

  const volumeCm3 = Math.abs(volume) / 1000; // mm3 -> cm3

  return {
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    boundingBox: {
      width: Math.round(((maxX - minX) / 10) * 100) / 100,  // mm -> cm
      height: Math.round(((maxY - minY) / 10) * 100) / 100,
      depth: Math.round(((maxZ - minZ) / 10) * 100) / 100,
    },
  };
};
